import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostBinding,
    HostListener,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { sleep } from '@cds/core/internal';
import { Subject, interval } from 'rxjs';
import { tap } from 'rxjs/operators';
import { fade } from 'shared';

import { DomComponent } from '../../../services';

export enum VerticalAnchor {
    Top = 'top',
    Center = 'center',
    Bottom = 'bottom',
}

export enum HorizontalAnchor {
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

@Component({
    selector: 'app-base-popup',
    templateUrl: './base-popup.component.html',
    styleUrls: ['./base-popup.component.scss'],
    animations: [fade('popup')],
    imports: [],
})
export class BasePopupComponent<T, R = unknown> implements OnInit, OnDestroy {
    protected elementRef = inject(ElementRef);

    protected readonly destroyRef = inject(DestroyRef);
    protected readonly cdr = inject(ChangeDetectorRef);

    @HostBinding('style.position')
    protected readonly stylePosition: string = 'absolute';

    @HostBinding('style.left')
    protected styleLeft = '0px';

    @HostBinding('style.top')
    protected styleTop = '0px';

    @HostBinding('style.pointer-events')
    protected stylePointerEvents: string = 'all';

    public verticalAnchor: VerticalAnchor = VerticalAnchor.Top;
    public horizontalAnchor: HorizontalAnchor = HorizontalAnchor.Center;

    public set origin(value: [number, number]) {
        requestAnimationFrame(() => {
            const width = this.elementRef.nativeElement.clientWidth;
            const height = this.elementRef.nativeElement.clientHeight;
            let [originX, originY] = value;

            switch (this.horizontalAnchor) {
                case HorizontalAnchor.Left:
                    break;
                case HorizontalAnchor.Center:
                    originX -= width / 2;
                    break;
                case HorizontalAnchor.Right:
                    originX -= width;
                    break;
            }

            switch (this.verticalAnchor) {
                case VerticalAnchor.Bottom:
                    originY -= height + (this.popupOptions.hasArrow ? 10 : 0);
                    break;
                case VerticalAnchor.Center:
                    originY -= height / 2;
                    break;
                case VerticalAnchor.Top:
                    originY += this.popupOptions.hasArrow ? 10 : 0;
                    break;
            }

            this._position[0] = originX;
            this._position[1] = originY;
            this.updateStyles();
            this.ensureSafePosition();
        });
    }

    private readonly _onClose = new Subject<R | undefined>();
    public readonly onClose = this._onClose.asObservable();

    private _position: [number, number] = [0, 0];
    // Internals
    public readonly __DOM_COMPONENT: DomComponent<T>;
    protected closable = false;
    private childPopups: Array<DomComponent<BasePopupComponent<unknown>>> = [];
    public popupOptions = {
        padding: 10,
        hasArrow: false,
        closeByEsc: true,
        closeByClickOutside: true,
        pointerEvents: 'all',
    };

    @HostBinding('@popup') protected popupAnimation = true;

    public constructor() {
        this.cdr = inject<ChangeDetectorRef>(ChangeDetectorRef);
    }

    @HostListener('document:click', ['$event'])
    public clickOutside($event: MouseEvent): void {
        const clickWithinComponent = this.elementRef.nativeElement.contains($event.target);
        const clickWithinChildPopup = this.childPopups
            .filter((child) => !child.isRemoved)
            .some((child) => child.elementRef.nativeElement.contains($event.target));
        if (this.popupOptions.closeByClickOutside && this.closable && !clickWithinComponent && !clickWithinChildPopup) {
            $event.stopImmediatePropagation();
            this.close();
        }
    }

    @HostListener('document:keydown.escape')
    public onEscapeDown(): void {
        if (this.popupOptions.closeByEsc) this.close();
    }

    public ngOnInit(): void {
        // Allow popup to close itself after a bit, to prevent it closing itself upon opening
        setTimeout(() => (this.closable = true), 100);
        // Have popup move itself to a safer position if needed continuously
        interval(100)
            .pipe(takeUntilDestroyed(this.destroyRef), tap(this.ensureSafePosition.bind(this)))
            .subscribe();
    }

    public ngOnDestroy(): void {
        if (!this._onClose.closed) this._onClose.complete();
    }

    public registerChildPopup(child: DomComponent<BasePopupComponent<unknown>>): void {
        if (!this.childPopups.includes(child)) this.childPopups.push(child);
    }

    public close(result?: R): void {
        if (!this._onClose.closed) {
            this._onClose.next(result);
            this._onClose.complete();
        }
        this.__DOM_COMPONENT.remove();
    }

    private async ensureSafePosition(): Promise<void> {
        await sleep(10);
        let left = this._position[0];
        let top = this._position[1];

        const width = this.elementRef.nativeElement.clientWidth;
        const height = this.elementRef.nativeElement.clientHeight;
        const padding = this.popupOptions.padding;

        // Calculate new position if needed
        if (top < padding) top = padding;
        if (left < padding) left = padding;
        if (top + height > window.innerHeight - padding) top = window.innerHeight - padding - height;
        if (left + width > window.innerWidth - padding) left = window.innerWidth - padding - width;

        this._position[0] = left;
        this._position[1] = top;
        this.updateStyles();
    }

    private updateStyles(): void {
        this.styleTop = `${this._position[1]}px`;
        this.styleLeft = `${this._position[0]}px`;
        this.stylePointerEvents = this.popupOptions.pointerEvents;
        this.cdr.markForCheck();
    }
}
